import React, { useState, useEffect } from "react";
import { Container, Col, Row, Card, CardBody, Table, Button } from "reactstrap";
import axios from "axios";
import { Link } from "react-router-dom";
// Rank images for 1st/2nd/3rd place
import firstPlaceImg from "../../assets/images/items/1st.png";
import secondPlaceImg from "../../assets/images/items/2nd.png";
import thirdPlaceImg from "../../assets/images/items/3rd.png";

// Reward item images
import rareCandyImg from "../../assets/images/items/candy.png";
import coinImg from "../../assets/images/items/coin.png";
import api from "../../api";

// Helper function for thousands separators
function formatNumber(num) {
  return num.toLocaleString();
}

const PokemonRankings = () => {
  // Page state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  // Current page data (Pokemon)
  const [pokemons, setPokemons] = useState([]);

  // Top three data for the description
  const [topThree, setTopThree] = useState([]);

  // Loading / error
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Document title
  document.title = "Pokémon Rankings";

  // ========== Fetch main page data ==========
  useEffect(() => {
    const fetchLeaderboard = async () => {
      setIsLoading(true);
      setError("");
      try {
        // Example: GET /v1/pokemon-rankings?page=X&limit=Y
        const response = await api.get(
          `/v1/pokemon-rankings?page=${page}&limit=${limit}`,
          { withCredentials: true },
        );
        // The backend returns { data, page, limit, total }
        const data = response.data.data || [];
        const totalCount = response.data.total || 0;

        setPokemons(data);
        setTotal(totalCount);
      } catch (err) {
        console.error(err);
        setError("Failed to load Pokémon leaderboard");
      } finally {
        setIsLoading(false);
      }
    };
    fetchLeaderboard();
  }, [page, limit]);

  const categoryColor = (pokemonName) => {
    if (pokemonName.toLowerCase().includes("cursed")) return "#6E6E6E";
    if (pokemonName.toLowerCase().includes("golden")) return "#ebbe00";
    if (pokemonName.toLowerCase().includes("diamond")) return "#85c7ff";
    if (pokemonName.toLowerCase().includes("rainbow")) return "#FF0000";
    if (pokemonName.toLowerCase().includes("shadow")) return "#2a0c32";
    return "#fffff";
  };

  // ========== Fetch top three for the description ==========
  useEffect(() => {
    const fetchTopThree = async () => {
      try {
        const response = await api.get("/v1/pokemon-rankings?page=1&limit=3", {
          withCredentials: true,
        });
        // The top three
        const data = response.data.data || [];
        setTopThree(data);
      } catch (err) {
        console.error("Failed to fetch top three Pokémon", err);
      }
    };
    fetchTopThree();
  }, []);

  // ========== Pagination Helpers ==========
  const totalPages = Math.ceil(total / limit);

  const handlePrev = () => {
    if (page > 1) {
      setPage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      setPage((prev) => prev + 1);
    }
  };

  function getLevel(exp) {
    const lvl = Math.floor(Math.pow(exp, 1 / 3));
    // If (lvl+1)^3 <= exp, increment level
    return Math.pow(lvl + 1, 3) <= exp ? lvl + 1 : lvl;
  }

  // ========== Top 3 Description ==========
  // 1st => 1 Rare Candy + 250 coins
  // 2nd => 1 Rare Candy
  // 3rd => 250 coins
  const topThreeDescription =
    topThree.length === 3 ? (
      <div style={{ lineHeight: "1.6", color: "white" }}>
        <p>
          Congratulations to the top 3 Pokémon:{" "}
          <strong>{topThree.map((p) => p.name).join(", ")}</strong>.
        </p>
        <p>
          1st place: 1 Rare Candy{" "}
          <img
            src={rareCandyImg}
            alt="Rare Candy"
            style={{
              width: "24px",
              verticalAlign: "middle",
              marginLeft: "5px",
              marginRight: "5px",
            }}
          />
          +
          <img
            src={coinImg}
            alt="Coins"
            style={{
              width: "24px",
              verticalAlign: "middle",
              marginLeft: "5px",
            }}
          />
          250
          <br />
          2nd place: 1 Rare Candy{" "}
          <img
            src={rareCandyImg}
            alt="Rare Candy"
            style={{
              width: "24px",
              verticalAlign: "middle",
              marginLeft: "5px",
            }}
          />
          <br />
          3rd place:
          <img
            src={coinImg}
            alt="Coins"
            style={{
              width: "24px",
              verticalAlign: "middle",
              marginLeft: "5px",
            }}
          />
          250
        </p>
        <p>
          <em>All rewards will be distributed at the end of the day. These rewards won't be given until battling is implemented.</em>{" "}
          (Banned or frozen trainers are not included.)
        </p>
      </div>
    ) : (
      "Loading top Pokémon..."
    );

  // ========== Rank Image ==========
  const getRankImage = (rank) => {
    if (rank === 1) return firstPlaceImg;
    if (rank === 2) return secondPlaceImg;
    if (rank === 3) return thirdPlaceImg;
    return null;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col md={12}>
              <Card>
                <CardBody>
                  <h4 className="mb-4">Pokémon Rankings</h4>

                  {/* Top-3 info 
                  <div
                    style={{
                      marginBottom: "20px",
                      padding: "15px",
                      backgroundColor: "#2a2f38",
                      borderRadius: "8px",
                      textAlign: "center",
                      fontSize: "1rem",
                      color: "#333",
                    }}
                  >
                    {topThreeDescription}
                  </div>
*/}
                  {isLoading && <div>Loading leaderboard...</div>}
                  {error && <div className="text-danger">{error}</div>}

                  {!isLoading && !error && (
                    <div>
                      <Table striped responsive className="align-middle">
                        <thead>
                          <tr>
                            <th style={{ width: "5%" }}>Rank</th>
                            <th style={{ width: "10%" }}>Pokémon</th>
                            <th>Name</th>
                            <th>Level</th>
                            <th>Trainer</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pokemons.map((pokemon, index) => {
                            // rank for this row
                            const rank = (page - 1) * limit + (index + 1);
                            const level = getLevel(pokemon.totalexp);
                            return (
                              <tr key={`${pokemon.id}-${pokemon.name}`}>
                                <td>
                                  {rank <= 3 ? (
                                    <img
                                      src={getRankImage(rank)}
                                      alt={`${rank} rank`}
                                    />
                                  ) : (
                                    rank
                                  )}
                                </td>
                                {/* Show the Pokémon sprite from /sprites/<name>.png */}
                                <td>
                                  <img
                                    src={`/sprites/${pokemon.name}.png`}
                                    alt={pokemon.name}
                                    style={{ width: 40, height: 40 }}
                                  />
                                </td>
                                <td
                                  style={{
                                    color: categoryColor(pokemon.name),
                                    fontWeight: "bold",
                                  }}
                                >
                                  {pokemon.name}
                                </td>
                                <td>{formatNumber(level)}</td>
                                <td>
                                  <Link
                                    to={`/playerprofile?user=${pokemon.owner}`}
                                    style={{
                                      textDecoration: "none",
                                      color: "white",
                                    }}
                                  >
                                    {pokemon.owner}{" "}
                                  </Link>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>

                      {/* Pagination controls */}
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                          <Button
                            color="primary"
                            disabled={page <= 1}
                            onClick={handlePrev}
                          >
                            Prev
                          </Button>{" "}
                          <Button
                            color="primary"
                            disabled={page >= totalPages}
                            onClick={handleNext}
                          >
                            Next
                          </Button>
                        </div>
                        <div>
                          Page {page} of {totalPages || 1}
                        </div>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PokemonRankings;
